import React, { useState, useEffect, useContext } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, onSnapshot } from "firebase/firestore";
import { auth } from "../firebase";
import UserContext from "../context/UserContext";
import { useTheme } from "../context/ThemeContext";
import { FaPlus, FaThumbsUp, FaComment, FaTrash, FaEdit, FaThumbtack, FaArrowUp, FaArrowDown } from "react-icons/fa";

const Forum = () => {
  const { user, userData } = useContext(UserContext);
  const { theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({ title: "", content: "" });
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [showComments, setShowComments] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPosts, setFilteredPosts] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "forum"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const postsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        postsData.push({
          id: doc.id,
          ...data,
          pinned: data.pinned || false, // Ensure pinned field exists
          priority: data.priority || 0 // Ensure priority field exists
        });
      });

      // Sort posts: pinned first (by priority), then unpinned by createdAt
      postsData.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        if (a.pinned && b.pinned) {
          return a.priority - b.priority; // Lower priority number = higher priority
        }
        return b.createdAt?.toDate() - a.createdAt?.toDate();
      });

      setPosts(postsData);
    }, (error) => {
      // Handle permission errors gracefully, especially during logout
      if (error.code === 'permission-denied') {
        console.warn('Permission denied accessing forum posts, likely due to logout');
        setPosts([]);
      } else {
        console.error('Error fetching forum posts:', error);
      }
    });

    return () => unsubscribe();
  }, []);

  // Filter posts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.authorName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPosts(filtered);
    }
  }, [posts, searchTerm]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) return;

    try {
      await addDoc(collection(db, "forum"), {
        title: newPost.title.trim(),
        content: newPost.content.trim(),
        authorId: user.uid,
        authorName: userData?.username || user.displayName || user.email,
        createdAt: new Date(),
        likes: [],
        commentCount: 0,
        pinned: false
      });
      setNewPost({ title: "", content: "" });
      setShowNewPostForm(false);
    } catch (error) {
      console.error("Error creating post:", error);
      alert("Failed to create post. Please try again.");
    }
  };

  const handleEditPost = async (postId, updatedData) => {
    try {
      await updateDoc(doc(db, "forum", postId), updatedData);
      setEditingPost(null);
    } catch (error) {
      console.error("Error updating post:", error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await deleteDoc(doc(db, "forum", postId));
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    }
  };

  const handleLikePost = async (postId, currentLikes) => {
    const updatedLikes = currentLikes.includes(user.uid)
      ? currentLikes.filter(id => id !== user.uid)
      : [...currentLikes, user.uid];

    try {
      await updateDoc(doc(db, "forum", postId), { likes: updatedLikes });
    } catch (error) {
      console.error("Error updating likes:", error);
    }
  };

  const handlePinPost = async (postId, isPinned) => {
    try {
      const updateData = { pinned: !isPinned };
      if (!isPinned) {
        // When pinning, set priority to current timestamp to maintain order
        updateData.priority = Date.now();
      }
      await updateDoc(doc(db, "forum", postId), updateData);
    } catch (error) {
      console.error("Error updating pin status:", error);
    }
  };

  const handlePriorityChange = async (postId, direction) => {
    try {
      const currentPost = posts.find(p => p.id === postId);
      if (!currentPost) return;

      const pinnedPosts = posts.filter(p => p.pinned).sort((a, b) => a.priority - b.priority);
      const currentIndex = pinnedPosts.findIndex(p => p.id === postId);

      if (direction === 'up' && currentIndex > 0) {
        // Swap with the post above
        const postAbove = pinnedPosts[currentIndex - 1];
        await updateDoc(doc(db, "forum", postId), { priority: postAbove.priority - 1 });
        await updateDoc(doc(db, "forum", postAbove.id), { priority: currentPost.priority });
      } else if (direction === 'down' && currentIndex < pinnedPosts.length - 1) {
        // Swap with the post below
        const postBelow = pinnedPosts[currentIndex + 1];
        await updateDoc(doc(db, "forum", postId), { priority: postBelow.priority + 1 });
        await updateDoc(doc(db, "forum", postBelow.id), { priority: currentPost.priority });
      }
    } catch (error) {
      console.error("Error updating priority:", error);
    }
  };

  const handleCreateComment = async (postId, commentText) => {
    if (!commentText.trim()) return;

    try {
      await addDoc(collection(db, "forum", postId, "comments"), {
        content: commentText,
        authorId: user.uid,
        authorName: userData?.username || user.displayName || user.email,
        createdAt: new Date()
      });

      // Update comment count
      const postRef = doc(db, "forum", postId);
      const post = posts.find(p => p.id === postId);
      await updateDoc(postRef, { commentCount: (post.commentCount || 0) + 1 });

      setNewComment({ ...newComment, [postId]: "" });
    } catch (error) {
      console.error("Error creating comment:", error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "forum", postId, "comments", commentId));

      // Update comment count
      const postRef = doc(db, "forum", postId);
      const post = posts.find(p => p.id === postId);
      await updateDoc(postRef, { commentCount: Math.max(0, (post.commentCount || 0) - 1) });
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const loadComments = async (postId) => {
    if (comments[postId]) {
      setShowComments({ ...showComments, [postId]: !showComments[postId] });
      return;
    }

    try {
      const q = query(collection(db, "forum", postId, "comments"), orderBy("createdAt", "asc"));
      // Set up real-time listener for comments
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const commentsData = [];
        querySnapshot.forEach((doc) => {
          commentsData.push({ id: doc.id, ...doc.data() });
        });
        setComments(prevComments => ({ ...prevComments, [postId]: commentsData }));
        setShowComments(prevShow => ({ ...prevShow, [postId]: true }));
      }, (error) => {
        // Handle permission errors gracefully, especially during logout
        if (error.code === 'permission-denied') {
          console.warn('Permission denied accessing forum comments, likely due to logout');
          setComments(prevComments => ({ ...prevComments, [postId]: [] }));
        } else {
          console.error('Error fetching forum comments:', error);
        }
      });

      // Store unsubscribe function to clean up later if needed
      // For now, we'll let it run continuously since comments are loaded on demand
    } catch (error) {
      console.error("Error loading comments:", error);
    }
  };

  const getThemeClasses = () => {
    switch (theme) {
      case "kids":
        return {
          card: "bg-silver border-2 border-kids-blue rounded-custom shadow-lg",
          button: "bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600",
          input: "border-2 border-kids-blue rounded-custom px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-kids-blue text-gray-800",
          text: "text-teens-gray"
        };
      case "teens":
        return {
          card: "bg-silver border-2 border-teal-500 rounded-custom shadow-lg",
          button: "bg-teal-500 text-white px-4 py-2 rounded-custom hover:bg-teal-600",
          input: "border-2 border-teal-500 rounded-custom px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-800",
          text: "text-teens-gray"
        };
      case "adults":
        return {
          card: "bg-silver border-2 border-gray-600 rounded-custom shadow-lg",
          button: "bg-gray-600 text-white px-4 py-2 rounded-custom hover:bg-gray-700",
          input: "border-2 border-gray-600 rounded-custom px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-gray-600 text-gray-800",
          text: "text-teens-gray"
        };
      default:
        return {
          card: "bg-silver border-2 border-kids-blue rounded-custom shadow-lg",
          button: "bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600",
          input: "border-2 border-kids-blue rounded-custom px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-kids-blue text-gray-800",
          text: "text-teens-gray"
        };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <div className="min-h-screen container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className={`text-3xl font-bold ${themeClasses.text}`}>Community Forum</h1>
        <button
          onClick={() => setShowNewPostForm(!showNewPostForm)}
          className={`${themeClasses.button} flex items-center gap-2`}
        >
          <FaPlus /> New Post
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${themeClasses.input} flex-1`}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                // Search is handled by useEffect
              }
            }}
          />
          <button
            onClick={() => setSearchTerm("")}
            className={`${themeClasses.button} ${searchTerm ? '' : 'opacity-50 cursor-not-allowed'}`}
            disabled={!searchTerm}
          >
            Clear
          </button>
        </div>
      </div>

      {showNewPostForm && (
        <div className={`${themeClasses.card} p-6 mb-8`}>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Create New Post</h2>
          <form onSubmit={handleCreatePost}>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Post Title"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                className={themeClasses.input}
                required
              />
            </div>
            <div className="mb-4">
              <textarea
                placeholder="What's on your mind?"
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                className={`${themeClasses.input} h-32 resize-none`}
                required
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className={themeClasses.button}>
                Post
              </button>
              <button
                type="button"
                onClick={() => setShowNewPostForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-custom hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-6">
        {filteredPosts.map((post) => (
          <div key={post.id} className={`${themeClasses.card} p-6`}>
            {editingPost === post.id ? (
              <div>
                <input
                  type="text"
                  value={editingPost === post.id ? post.title : ""}
                  onChange={(e) => {
                    const updatedPosts = posts.map(p =>
                      p.id === post.id ? { ...p, title: e.target.value } : p
                    );
                    setPosts(updatedPosts);
                  }}
                  className={`${themeClasses.input} mb-2`}
                />
                <textarea
                  value={editingPost === post.id ? post.content : ""}
                  onChange={(e) => {
                    const updatedPosts = posts.map(p =>
                      p.id === post.id ? { ...p, content: e.target.value } : p
                    );
                    setPosts(updatedPosts);
                  }}
                  className={`${themeClasses.input} h-32 resize-none mb-4`}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditPost(post.id, { title: post.title, content: post.content })}
                    className={themeClasses.button}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingPost(null)}
                    className="bg-gray-500 text-white px-4 py-2 rounded-custom hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      {post.pinned && <FaThumbtack className="text-yellow-500" title="Pinned Post" />}
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      By {post.authorName} on {post.createdAt?.toDate().toLocaleDateString()}
                    </p>
                  </div>
                  {user.email === "adamkfrancis1989@gmail.com" && (
                    <div className="flex gap-2">
                      {post.pinned && (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handlePriorityChange(post.id, 'up')}
                            className="text-gray-500 hover:text-blue-600 p-1"
                            title="Move Up"
                          >
                            <FaArrowUp size={12} />
                          </button>
                          <button
                            onClick={() => handlePriorityChange(post.id, 'down')}
                            className="text-gray-500 hover:text-blue-600 p-1"
                            title="Move Down"
                          >
                            <FaArrowDown size={12} />
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => handlePinPost(post.id, post.pinned || false)}
                        className={`${post.pinned ? 'text-yellow-500' : 'text-gray-500'} hover:text-yellow-600`}
                        title={post.pinned ? "Unpin Post" : "Pin Post"}
                      >
                        <FaThumbtack />
                      </button>
                      <button
                        onClick={() => setEditingPost(post.id)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                  {(post.authorId === user.uid && user.email !== "adamkfrancis1989@gmail.com") && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingPost(post.id)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-gray-800 mb-4 whitespace-pre-wrap">{post.content}</p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleLikePost(post.id, post.likes || [])}
                    className={`flex items-center gap-1 ${post.likes?.includes(user.uid) ? 'text-blue-500' : 'text-gray-500'} hover:text-blue-600`}
                  >
                    <FaThumbsUp /> {post.likes?.length || 0}
                  </button>
                  <button
                    onClick={() => loadComments(post.id)}
                    className="flex items-center gap-1 text-gray-500 hover:text-blue-600"
                  >
                    <FaComment /> {post.commentCount || 0}
                  </button>
                </div>

                {showComments[post.id] && comments[post.id] && (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">Comments</h4>
                    <div className="space-y-4 mb-4">
                      {comments[post.id].map((comment) => (
                        <div key={comment.id} className="bg-gray-50 p-3 rounded-custom">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-semibold text-sm text-gray-800">{comment.authorName}</p>
                              <p className="text-sm text-gray-600">
                                {comment.createdAt?.toDate().toLocaleDateString()}
                              </p>
                              <p className="mt-2 text-gray-800">{comment.content}</p>
                            </div>
                            {(comment.authorId === user.uid || post.authorId === user.uid || user.email === "adamkfrancis1989@gmail.com") && (
                              <button
                                onClick={() => handleDeleteComment(post.id, comment.id)}
                                className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                                title="Delete comment"
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={newComment[post.id] || ""}
                        onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
                        className={`${themeClasses.input} flex-1`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateComment(post.id, newComment[post.id]);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleCreateComment(post.id, newComment[post.id])}
                        className={themeClasses.button}
                      >
                        Comment
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className={`text-xl ${themeClasses.text}`}>No posts yet. Be the first to start a discussion!</p>
        </div>
      )}

      {posts.length > 0 && filteredPosts.length === 0 && searchTerm && (
        <div className="text-center py-12">
          <p className={`text-xl ${themeClasses.text}`}>No posts found matching "{searchTerm}". Try a different search term.</p>
        </div>
      )}
    </div>
  );
};

export default Forum;
